import { getAuth } from "firebase/auth";

import { firebaseApp } from "../../firebase/firebaseApp";
import {
  normalizeClinicStudyIdInput,
  normalizeDateInput,
  normalizeMeasurementInput,
  normalizeParticipantIdInput,
} from "../validation/controlledInputs";

const APPROVED_REGIONAL_USER_PROFILE_FIELDS = new Set([
  "id",
  "subjectId",
  "clinicStudyId",
  "givenName",
  "familyName",
  "height",
  "weight",
  "chestCircumference",
  "dateOfBirth",
  "sexAtBirth",
  "ethnicity",
  "ethnicityOther",
  "respiratoryConditions",
  "respiratoryConditionsOther",
  "smoking",
  "yearsSinceQuit",
  "exQuitEffect",
]);

/**
 * Resolves the configured regional backend API base URL for the patient web
 * app.
 *
 * @param {NodeJS.ProcessEnv} [env=process.env] - Environment source.
 * @returns {string} Normalized base URL without a trailing slash.
 */
export function getRegionalApiBaseUrl(env = process.env) {
  const configuredBaseUrl = env.REACT_APP_PDX_API_BASE_URL?.trim();

  if (!configuredBaseUrl) {
    throw new Error(
      "Regional backend API URL is not configured. Set REACT_APP_PDX_API_BASE_URL.",
    );
  }

  return configuredBaseUrl.replace(/\/+$/, "");
}

async function getRequiredFirebaseIdToken(
  auth = getAuth(firebaseApp),
  forceRefresh = false,
) {
  const currentUser = auth.currentUser;

  if (!currentUser || typeof currentUser.getIdToken !== "function") {
    throw new Error(
      "A signed-in Firebase user is required for protected backend API requests.",
    );
  }

  return currentUser.getIdToken(forceRefresh);
}

async function readResponseBody(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getPreferredLocaleHeader() {
  if (typeof document !== "undefined") {
    const htmlLanguage = document.documentElement?.lang?.trim();

    if (htmlLanguage) {
      return htmlLanguage;
    }
  }

  if (typeof navigator !== "undefined") {
    return navigator.language || navigator.languages?.[0] || null;
  }

  return null;
}

/**
 * Calls a regional backend API route and parses the JSON response body.
 *
 * By default, requests include the current Firebase user's ID token as a
 * bearer token.
 *
 * @param {string} path - API path such as `/v1/users/me`.
 * @param {{
 *   method?: string,
 *   headers?: Record<string, string>,
 *   body?: unknown,
 *   requireAuth?: boolean,
 *   idToken?: string,
 *   auth?: import("firebase/auth").Auth,
 *   fetchImpl?: typeof fetch,
 *   baseUrl?: string
 * }} [options={}] - Request options and test overrides.
 * @returns {Promise<unknown>} Parsed JSON response or raw text body.
 */
export async function fetchRegionalApiJson(path, options = {}) {
  const {
    method = "GET",
    headers = {},
    body,
    requireAuth = true,
    idToken,
    auth,
    fetchImpl = fetch,
    baseUrl = getRegionalApiBaseUrl(),
    retryDelayMs = 300,
    retries,
  } = options;

  const isIdempotent = method === "GET" || method === "HEAD";
  // Idempotent requests are retried through transient backend failures by
  // default; callers can override via `retries`.
  const maxTransientRetries = retries ?? (isIdempotent ? 2 : 0);
  const transientStatuses = new Set([502, 503, 504]);
  const backoffDelay = (attempt) =>
    new Promise((resolve) => setTimeout(resolve, retryDelayMs * 2 ** attempt));

  const baseRequestHeaders = { ...headers };
  let requestBody = body;

  if (
    body !== undefined &&
    body !== null &&
    typeof body !== "string" &&
    !(body instanceof FormData)
  ) {
    if (
      !baseRequestHeaders["Content-Type"] &&
      !baseRequestHeaders["content-type"]
    ) {
      baseRequestHeaders["Content-Type"] = "application/json";
    }
    requestBody = JSON.stringify(body);
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const executeRequest = async (forceRefresh = false) => {
    const requestHeaders = { ...baseRequestHeaders };
    const localeHeader = getPreferredLocaleHeader();

    if (
      localeHeader &&
      !requestHeaders["x-pdx-locale"] &&
      !requestHeaders["X-PDX-Locale"]
    ) {
      requestHeaders["x-pdx-locale"] = localeHeader;
    }

    if (requireAuth) {
      const bearerToken =
        idToken || (await getRequiredFirebaseIdToken(auth, forceRefresh));
      requestHeaders.Authorization = `Bearer ${bearerToken}`;
    }

    return fetchImpl(`${baseUrl}${normalizedPath}`, {
      method,
      headers: requestHeaders,
      body: requestBody,
    });
  };

  let response;
  let attempt = 0;

  // Transparently retry idempotent requests through transient failures —
  // network errors and 502/503/504, e.g. Cloud Run cold starts after the
  // browser/laptop has been idle — with exponential backoff, so a warm-up blip
  // doesn't surface as an error the user has to fix by hitting refresh.
  for (;;) {
    try {
      response = await executeRequest();
    } catch (networkError) {
      if (attempt < maxTransientRetries) {
        await backoffDelay(attempt);
        attempt += 1;
        continue;
      }
      throw networkError;
    }

    if (response.status === 401 && requireAuth && !idToken) {
      response = await executeRequest(true);
    }

    if (
      transientStatuses.has(response.status) &&
      attempt < maxTransientRetries
    ) {
      await backoffDelay(attempt);
      attempt += 1;
      continue;
    }

    break;
  }

  const responseBody = await readResponseBody(response);

  if (!response.ok) {
    const error = new Error(`Regional backend request failed with ${response.status}.`);
    error.status = response.status;
    error.body = responseBody;
    throw error;
  }

  return responseBody;
}

/**
 * Fetches the authenticated user's backend account context from the regional
 * API gateway.
 *
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed `/v1/users/me` response.
 */
export function fetchRegionalUserContext(options = {}) {
  return fetchRegionalApiJson("/v1/users/me", options);
}

/**
 * Requests a password reset email through the public regional backend route.
 *
 * This keeps the browser talking only to the regional gateway while the
 * backend decides which reset sender implementation to use upstream.
 *
 * @param {{ email: string, origin: string, language?: string }} payload - Reset
 *   request payload. `language` is the user's selected UI language so the reset
 *   email is composed in that language (backend falls back to the forwarded
 *   `x-pdx-locale` header when omitted).
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ ok: boolean }>} Parsed password-reset response.
 */
export function requestRegionalPasswordReset(payload, options = {}) {
  return fetchRegionalApiJson("/v1/app/auth/password-reset", {
    method: "POST",
    body: payload,
    requireAuth: false,
    ...options,
  });
}

function normalizeMeasurementVariants(params = {}) {
  const variants = Array.isArray(params.variants)
    ? params.variants
    : params.variant
      ? [params.variant]
      : [];

  return variants
    .map((variant) => (typeof variant === "string" ? variant.trim() : ""))
    .filter(Boolean);
}

function buildMeasurementQuery(params = {}) {
  const searchParams = new URLSearchParams();

  normalizeMeasurementVariants(params)
    .forEach((variant) => searchParams.append("variant", variant));

  [
    "captureMode",
    "status",
    "recentWithinMs",
    "limit",
  ].forEach((key) => {
    const value = params[key];

    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function parseRegionalDateLike(value) {
  if (!value) return null;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (Number.isFinite(value?.seconds)) {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
  }

  return null;
}

function parseMeasurementIdTimestampMs(value) {
  const id = typeof value === "string" ? value.trim() : "";
  const match = id.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2}(?:\.\d+)?Z?)/u,
  );

  if (!match) return null;

  const parsed = Date.parse(`${match[1]}T${match[2]}:${match[3]}:${match[4]}`);
  return Number.isFinite(parsed) ? parsed : null;
}

function measurementRecordTimestampMs(record) {
  return (
    parseRegionalDateLike(record?.takenAt) ||
    parseRegionalDateLike(record?.completedAt) ||
    parseRegionalDateLike(record?.timestampIso) ||
    parseRegionalDateLike(record?.createdAt) ||
    parseMeasurementIdTimestampMs(
      record?.measurementId || record?.recordingId || record?.docId,
    ) ||
    0
  );
}

function sortMeasurementRecordsNewestFirst(records) {
  return [...records].sort((left, right) => {
    const rightTime = measurementRecordTimestampMs(right);
    const leftTime = measurementRecordTimestampMs(left);

    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }

    return String(
      right?.measurementId || right?.recordingId || right?.docId || "",
    ).localeCompare(
      String(left?.measurementId || left?.recordingId || left?.docId || ""),
    );
  });
}

function parsePositiveLimit(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function mergeSegmentedMeasurementResponses(responses, limitValue) {
  const limit = parsePositiveLimit(limitValue);
  const measurements = sortMeasurementRecordsNewestFirst(
    responses.flatMap((response) =>
      Array.isArray(response?.measurements) ? response.measurements : [],
    ),
  );
  const pageMeasurements = limit ? measurements.slice(0, limit) : measurements;

  return {
    ...(responses.find(Boolean) || {}),
    measurements: pageMeasurements,
    hasMore:
      responses.some((response) => Boolean(response?.hasMore)) ||
      Boolean(limit && measurements.length > limit),
  };
}

export function createRegionalMeasurementUploadSession(payload, options = {}) {
  return fetchRegionalApiJson("/v1/app/measurements/upload-sessions", {
    method: "POST",
    body: payload,
    ...options,
  });
}

export function fetchRegionalMeasurements(params = {}, options = {}) {
  const variants = normalizeMeasurementVariants(params);

  if (params.segmentVariants === true && variants.length > 1) {
    const perVariantLimit = params.perVariantLimit || params.limit;

    return Promise.all(
      variants.map((variant) =>
        fetchRegionalApiJson(
          `/v1/app/measurements${buildMeasurementQuery({
            ...params,
            variant,
            variants: undefined,
            limit: perVariantLimit,
          })}`,
          options,
        ),
      ),
    ).then((responses) =>
      mergeSegmentedMeasurementResponses(responses, params.limit),
    );
  }

  return fetchRegionalApiJson(
    `/v1/app/measurements${buildMeasurementQuery(params)}`,
    options,
  );
}

export function fetchRegionalMeasurement(
  { variant, measurementId, recordingId, docId } = {},
  options = {},
) {
  const resolvedId = measurementId || recordingId || docId;

  if (!variant || !resolvedId) {
    throw new Error("Measurement variant and id are required.");
  }

  return fetchRegionalApiJson(
    `/v1/app/measurements/${encodeURIComponent(variant)}/${encodeURIComponent(resolvedId)}`,
    options,
  );
}

export function fetchRegionalMeasurementSignals(
  { variant, measurementId, recordingId, docId, signalAlgorithm } = {},
  options = {},
) {
  const resolvedId = measurementId || recordingId || docId;

  if (!variant || !resolvedId) {
    throw new Error("Measurement variant and id are required.");
  }

  const query = new URLSearchParams();
  const normalizedSignalAlgorithm =
    typeof signalAlgorithm === "string" ? signalAlgorithm.trim() : "";

  if (normalizedSignalAlgorithm) {
    query.set("signalAlgorithm", normalizedSignalAlgorithm);
  }

  const queryString = query.toString();

  return fetchRegionalApiJson(
    `/v1/app/results/measurements/${encodeURIComponent(variant)}/${encodeURIComponent(resolvedId)}/signals${queryString ? `?${queryString}` : ""}`,
    options,
  );
}

export function completeRegionalMeasurementUploadSession(
  { variant, measurementId, recordingId, docId, ...payload } = {},
  options = {},
) {
  const resolvedId = measurementId || recordingId || docId;

  if (!variant || !resolvedId) {
    throw new Error("Measurement variant and id are required.");
  }

  return fetchRegionalApiJson(
    `/v1/app/measurements/${encodeURIComponent(variant)}/${encodeURIComponent(resolvedId)}/upload-complete`,
    {
      method: "POST",
      body: payload,
      ...options,
    },
  );
}

export function failRegionalMeasurementUploadSession(
  { variant, measurementId, recordingId, docId, ...payload } = {},
  options = {},
) {
  const resolvedId = measurementId || recordingId || docId;

  if (!variant || !resolvedId) {
    throw new Error("Measurement variant and id are required.");
  }

  return fetchRegionalApiJson(
    `/v1/app/measurements/${encodeURIComponent(variant)}/${encodeURIComponent(resolvedId)}/upload-error`,
    {
      method: "POST",
      body: payload,
      ...options,
    },
  );
}

export async function fetchRegionalMeasurementPresence(
  { variants, requireCompleted = false, recentWithinMs = null } = {},
  options = {},
) {
  const response = await fetchRegionalMeasurements(
    {
      variants,
      status: requireCompleted ? "complete" : undefined,
      recentWithinMs,
      limit: 100,
    },
    options,
  );
  const measurements = Array.isArray(response?.measurements)
    ? response.measurements
    : [];
  const byVariant = Object.fromEntries(
    (Array.isArray(variants) ? variants : []).map((variant) => [
      variant,
      measurements.some((entry) => entry?.variant === variant),
    ]),
  );

  return {
    byVariant,
    any: Object.values(byVariant).some(Boolean),
  };
}

export function recordRegionalLegalAction(payload, options = {}) {
  return fetchRegionalApiJson("/v1/legal/records", {
    method: "POST",
    body: payload,
    ...options,
  });
}

/**
 * Fetches the approved content (metadata + renderable body) for a legal
 * document from the regional backend, resolved for the requesting application
 * surface and locale. The Consent Service owns the content; the app renders
 * what it returns rather than a bundled local copy.
 *
 * @param {string} documentType - "terms" or "privacy".
 * @param {{ locale?: string, surface?: string }} [params={}] - Resolution inputs.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ status: string, document: object }>} Resolved document.
 */
export function fetchRegionalLegalDocument(documentType, params = {}, options = {}) {
  const { locale, surface = "app" } = params;
  const query = new URLSearchParams({ documentType });

  if (locale) {
    query.set("locale", locale);
  }

  if (surface) {
    query.set("surface", surface);
  }

  return fetchRegionalApiJson(`/v1/legal/documents?${query.toString()}`, {
    requireAuth: false,
    ...options,
  });
}

/**
 * Submits questionnaire responses through the protected regional backend route.
 *
 * This keeps questionnaire validation and persistence owned by the backend
 * service rather than direct browser writes.
 *
 * @param {string} questionnaireId - Questionnaire slug such as `r-scale`.
 * @param {{
 *   responses: Record<string, unknown>,
 *   submittedAt?: string,
 *   locale?: string,
 *   contentVersion?: string
 * }} payload - Questionnaire response payload.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed questionnaire submission response.
 */
export function submitRegionalQuestionnaireResponse(
  questionnaireId,
  payload,
  options = {},
) {
  const normalizedQuestionnaireId = encodeURIComponent(
    String(questionnaireId || "").trim(),
  );

  return fetchRegionalApiJson(
    `/v1/app/questionnaires/${normalizedQuestionnaireId}/responses`,
    {
      method: "POST",
      body: payload,
      ...options,
    },
  );
}

/**
 * Fetches the authenticated user's questionnaire history for task/adherence UI.
 *
 * @param {{ questionnaireId?: string, limit?: number }} [params={}] - Optional history filters.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ questionnaires: Array<object> }>} Parsed questionnaire history response.
 */
export function fetchRegionalQuestionnaireHistory(params = {}, options = {}) {
  const { questionnaireId, limit } = params;
  const query = new URLSearchParams();

  if (questionnaireId) {
    query.set("questionnaireId", String(questionnaireId).trim());
  }

  if (Number.isInteger(limit) && limit > 0) {
    query.set("limit", String(limit));
  }

  const queryString = query.toString();
  const suffix = queryString ? `?${queryString}` : "";
  return fetchRegionalApiJson(`/v1/app/questionnaires/history${suffix}`, options);
}

/**
 * Fetches the authenticated user's backend-owned protocol task board.
 *
 * The backend resolves applicable protocol config plus submitted recording and
 * questionnaire activity so the browser does not query Firebase collections
 * directly for task/adherence UI.
 *
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ status: string, protocolConfig: object|null, contextMetadata: object, taskBoard: object }>}
 * Parsed task-board response.
 */
export function fetchRegionalProtocolTaskBoard(options = {}) {
  return fetchRegionalApiJson("/v1/app/tasks", options);
}

/**
 * Performs the explicit post-auth session bootstrap call for a signed-in user.
 *
 * This is the current placeholder integration point for backend-owned user
 * creation and login-audit writes. For now it ensures the backend session
 * record exists and records the latest authenticated handoff timestamp.
 *
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed `/v1/users/session/bootstrap` response.
 */
export function bootstrapRegionalUserSession(options = {}) {
  return fetchRegionalApiJson("/v1/users/session/bootstrap", {
    method: "POST",
    ...options,
  });
}

/**
 * Completes the regional backend post-auth handoff for a freshly authenticated
 * Firebase user using that user's current ID token directly.
 *
 * Passing the token explicitly avoids racing `auth.currentUser` immediately
 * after login or sign-up.
 *
 * @param {{ getIdToken?: (forceRefresh?: boolean) => Promise<string> }} firebaseUser - Authenticated Firebase user.
 * @param {Parameters<typeof bootstrapRegionalUserSession>[0]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed session-bootstrap response.
 */
export async function completeRegionalAuthHandoff(
  firebaseUser,
  options = {},
) {
  if (!firebaseUser || typeof firebaseUser.getIdToken !== "function") {
    throw new Error(
      "A signed-in Firebase user is required to bootstrap the regional backend session.",
    );
  }

  const idToken = await firebaseUser.getIdToken(false);

  return bootstrapRegionalUserSession({
    ...options,
    idToken,
  });
}

function cleanProfileValue(value) {
  if (value === undefined || value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => cleanProfileValue(entry))
          .filter(Boolean),
      ),
    );
  }

  if (typeof value === "string") {
    const next = value.trim();
    return next.length > 0 ? next : null;
  }

  return value;
}

function normalizeProfileValue(field, value) {
  if (value === undefined || value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => cleanProfileValue(entry))
          .filter(Boolean),
      ),
    );
  }

  if (field === "id" || field === "subjectId") {
    const next = normalizeParticipantIdInput(value);
    return next || null;
  }

  if (field === "clinicStudyId") {
    const next = normalizeClinicStudyIdInput(value);
    return next || null;
  }

  if (field === "dateOfBirth") {
    const next = normalizeDateInput(value);
    return next || null;
  }

  if (
    field === "height" ||
    field === "weight" ||
    field === "chestCircumference"
  ) {
    const next = normalizeMeasurementInput(value, { fieldName: field });
    return next || null;
  }

  return cleanProfileValue(value);
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .map((entry) => cleanProfileValue(entry))
        .filter(Boolean),
    ),
  );
}

function normalizeTaskAssignmentList(value, idKey) {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set();
  const normalizedAssignments = [];

  for (const entry of value) {
    const candidate = entry && typeof entry === "object" ? entry : {};
    const taskId = cleanProfileValue(candidate[idKey]);

    if (!taskId || seen.has(taskId)) {
      continue;
    }

    seen.add(taskId);
    normalizedAssignments.push({
      [idKey]: taskId,
      required: candidate.required === true,
      frequency: cleanProfileValue(candidate.frequency) || null,
    });
  }

  return normalizedAssignments;
}

const USER_WEB_APP_FEATURE_ACCESS_SCHEMA = Object.freeze({
  results: Object.freeze([
    "rr",
    "hr",
    "hrv",
    "tv",
    "fev1",
    "fvc",
    "pef",
    "fev1Fvc",
    "flowLoops",
    "relativeChange",
    "resultsTrends",
    "cardiacQuality",
    "signalQuality",
    "spirometryQuality",
  ]),
  elements: Object.freeze([
    "poincareChart",
    "respiratorySignalChart",
    "spirometrySegmentChart",
  ]),
  misc: Object.freeze(["weatherDetails"]),
});

function normalizeUserWebAppFeatureAccess(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return Object.entries(USER_WEB_APP_FEATURE_ACCESS_SCHEMA).reduce(
    (access, [sectionKey, featureKeys]) => {
      access[sectionKey] = featureKeys.reduce((sectionAccess, featureKey) => {
        sectionAccess[featureKey] = value?.[sectionKey]?.[featureKey] === true;
        return sectionAccess;
      }, {});
      return access;
    },
    {},
  );
}

function normalizeRespiratoryConditionsValue(value) {
  if (Array.isArray(value)) {
    return normalizeStringArray(value);
  }

  const normalizedValue = cleanProfileValue(value);
  return normalizedValue ? [normalizedValue] : [];
}

/**
 * Normalizes a regional backend profile payload into the legacy app-friendly
 * shape used by existing form and prefill flows.
 *
 * The backend owns `subjectId`, while the current web-app form code still
 * expects `id`.
 *
 * @param {unknown} profile - Raw backend profile payload.
 * @returns {Record<string, unknown> | null} Normalized app profile or null.
 */
export function normalizeRegionalUserProfile(profile) {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    return null;
  }

  const source = { ...profile };
  const normalizedProfile = {};
  const subjectId = normalizeProfileValue("subjectId", source.subjectId ?? source.id);

  delete source.subjectId;

  if (subjectId) {
    normalizedProfile.id = subjectId;
  }

  for (const [key, value] of Object.entries(source)) {
    if (key === "respiratoryConditions") {
      const normalizedValues = normalizeRespiratoryConditionsValue(value);
      normalizedProfile[key] =
        normalizedValues.length > 0 ? normalizedValues : null;
      continue;
    }

    normalizedProfile[key] = normalizeProfileValue(key, value);
  }

  return normalizedProfile;
}

/**
 * Normalizes the clinic-scoped recording-mode config returned during backend
 * bootstrap so route gating can treat it as trusted server-owned state.
 *
 * @param {unknown} config - Raw backend recording-mode config payload.
 * @returns {{
 *   permittedModes: string[],
 *   permittedQuestionnaires: string[],
 *   recordingModeAssignments: Array<{ mode: string, required: boolean, frequency: string | null }>,
 *   questionnaireAssignments: Array<{ questionnaireId: string, required: boolean, frequency: string | null }>,
 *   reminderEmailConfig: { enabled: boolean },
 *   userWebAppFeatureAccess: {
 *     results: Record<string, boolean>,
 *     elements: Record<string, boolean>,
 *     misc: Record<string, boolean>
 *   } | null,
 *   defaultMode: string | null,
 *   spirometryVideoRetentionMode: string | null
 * } | null} Normalized clinic configuration or null.
 */
export function normalizeRegionalRecordingModeConfig(config) {
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return null;
  }

  return {
    permittedModes: normalizeStringArray(config.permittedModes ?? config.modes),
    permittedQuestionnaires: normalizeStringArray(
      config.permittedQuestionnaires,
    ),
    recordingModeAssignments: normalizeTaskAssignmentList(
      config.recordingModeAssignments,
      "mode",
    ),
    questionnaireAssignments: normalizeTaskAssignmentList(
      config.questionnaireAssignments,
      "questionnaireId",
    ),
    reminderEmailConfig: {
      enabled: config?.reminderEmailConfig?.enabled !== false,
    },
    userWebAppFeatureAccess: normalizeUserWebAppFeatureAccess(
      config.userWebAppFeatureAccess,
    ),
    defaultMode: cleanProfileValue(config.defaultMode),
    spirometryVideoRetentionMode: cleanProfileValue(
      config.spirometryVideoRetentionMode,
    ),
  };
}

/**
 * Builds the backend PATCH payload for a legacy app profile object.
 *
 * @param {Record<string, unknown>} profile - App profile/form data.
 * @returns {{ profile: Record<string, unknown> }} Backend PATCH payload.
 */
export function buildRegionalUserProfilePatch(profile) {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    throw new Error("Regional user profile updates require a plain object.");
  }

  const backendProfile = {};

  for (const [key, value] of Object.entries(profile)) {
    if (!APPROVED_REGIONAL_USER_PROFILE_FIELDS.has(key)) {
      continue;
    }

    if (key === "id") {
      backendProfile.subjectId = normalizeProfileValue("subjectId", value);
      continue;
    }

    backendProfile[key] =
      key === "respiratoryConditions"
        ? normalizeRespiratoryConditionsValue(value)
        : normalizeProfileValue(key, value);
  }

  if (backendProfile.ethnicity !== "Other") {
    backendProfile.ethnicityOther = null;
  }

  if (
    !Array.isArray(backendProfile.respiratoryConditions) ||
    !backendProfile.respiratoryConditions.includes("other")
  ) {
    backendProfile.respiratoryConditionsOther = null;
  }

  return {
    profile: backendProfile,
  };
}

/**
 * Partially updates the authenticated user's backend-owned user context.
 *
 * @param {Record<string, unknown>} payload - Backend user-context PATCH payload.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed `/v1/users/me` response.
 */
export function updateRegionalUserContext(payload, options = {}) {
  return fetchRegionalApiJson("/v1/users/me", {
    method: "PATCH",
    body: payload,
    ...options,
  });
}

/**
 * Saves the authenticated user's app profile through the regional backend.
 *
 * @param {Record<string, unknown>} profile - Legacy app profile/form data.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<unknown>} Parsed `/v1/users/me` response.
 */
export function saveRegionalUserProfile(profile, options = {}) {
  return updateRegionalUserContext(
    buildRegionalUserProfilePatch(profile),
    options,
  );
}

/**
 * Validates a clinic/subject pairing through the regional backend.
 *
 * @param {{ clinicStudyId?: string, subjectId?: string }} payload - Candidate clinic/subject pairing.
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ ok: boolean, reason?: string }>} Validation result.
 */
export function validateRegionalClinicAssociation(payload, options = {}) {
  return fetchRegionalApiJson("/v1/users/clinic-association/validate", {
    method: "POST",
    body: payload,
    ...options,
  });
}

export function markRegionalSubjectInviteViewed(payload, options = {}) {
  return fetchRegionalApiJson("/v1/app/subject-invites/viewed", {
    method: "POST",
    body: payload,
    ...options,
  });
}

/**
 * Fetches the authenticated user's latest profile summary from the regional
 * backend.
 *
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ latestMs: number | null, newestName: string | null }>} Parsed profile-info response.
 */
export function fetchRegionalUserProfileInfo(options = {}) {
  return fetchRegionalApiJson("/v1/users/profile/info", options);
}

/**
 * Fetches the authenticated user's latest stored profile JSON through the
 * regional backend.
 *
 * @param {Parameters<typeof fetchRegionalApiJson>[1]} [options={}] - Request options.
 * @returns {Promise<{ profile: unknown, latestMs: number | null, newestName: string | null }>}
 * Parsed profile response.
 */
export function fetchRegionalUserProfile(options = {}) {
  return fetchRegionalApiJson("/v1/users/profile", options);
}
